import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Lightweight, dependency-free emoji picker.
 * Avoids pulling in a 300KB+ npm package; uses a curated set of the
 * most common platform-relevant emojis grouped into tabs.
 *
 * The "Frequent" tab is personalised: every time the user picks an
 * emoji we remember it locally (most-recent first, deduped, capped at
 * 12) so the tab quietly learns their preferences. If nothing has
 * been picked yet — or if localStorage is unavailable / corrupted —
 * we fall back to the curated default list.
 */
const DEFAULT_FREQUENT = ['👍', '❤️', '🔥', '🎉', '😂', '🙌', '🙏', '👏', '✨', '💯', '🤝', '👋'];

const EMOJI_GROUPS = {
  Smileys: ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '☺️', '😚', '😙', '🥲', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔'],
  Gestures: ['👍', '👎', '👌', '🤌', '🤏', '✌️', '🤞', '🫰', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👋', '🤚', '🖐️', '✋', '🖖', '🫱', '🫲', '🫳', '🫴', '👏', '🙌', '🙏', '✍️'],
  Hearts: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟'],
  Work: ['💼', '🎬', '🎥', '📽️', '🎞️', '📸', '📷', '🎤', '🎧', '🎵', '🎶', '🎭', '🎨', '🎪', '🎟️', '🎫', '🎯', '🏆', '🥇', '🥈', '🥉', '🏅', '📝', '📋', '📅', '📆', '🗓️', '⏰', '⏲️', '⌛', '✅', '❌'],
  Celebrate: ['🎉', '🎊', '🎈', '🎁', '🎂', '🍾', '🥂', '🍻', '🥳', '🎆', '🎇', '✨', '⭐', '🌟', '💫', '🏁', '🚀'],
};

const RECENT_KEY = 'ccuk.emoji.recent.v1';
const RECENT_MAX = 12;

function readRecent() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((e) => typeof e === 'string' && e.length > 0).slice(0, RECENT_MAX);
  } catch {
    return [];
  }
}

function writeRecent(list) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    // Quota / disabled / private mode — silently no-op, picker still works.
  }
}

export default function EmojiPicker({ onSelect, onClose }) {
  const [tab, setTab] = useState('Frequent');
  const [recent, setRecent] = useState(() => readRecent());
  const ref = useRef(null);

  const frequent = useMemo(
    () => (recent.length > 0 ? recent : DEFAULT_FREQUENT),
    [recent],
  );

  const tabs = useMemo(() => ['Frequent', ...Object.keys(EMOJI_GROUPS)], []);

  const handlePick = useCallback(
    (emoji) => {
      setRecent((prev) => {
        const next = [emoji, ...prev.filter((e) => e !== emoji)].slice(0, RECENT_MAX);
        writeRecent(next);
        return next;
      });
      onSelect?.(emoji);
    },
    [onSelect],
  );

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };

    document.addEventListener('pointerdown', onDoc);
    return () => document.removeEventListener('pointerdown', onDoc);
  }, [onClose]);

  const visibleEmojis = tab === 'Frequent' ? frequent : EMOJI_GROUPS[tab] || DEFAULT_FREQUENT;

  return (
    <div
      ref={ref}
      className="
        w-full
        bg-theme-surface
        border-y border-theme/70
        sm:border sm:border-theme/70
        rounded-t-2xl rounded-b-none
        sm:rounded-2xl
        shadow-[0_-10px_30px_rgba(0,0,0,0.16)]
        sm:shadow-2xl
        p-3 sm:p-4
        overflow-hidden
      "
      data-testid="emoji-picker"
    >
      <div
        className="
          grid grid-cols-3 sm:grid-cols-6
          gap-x-2 gap-y-1
          mb-3
          border-b border-theme/40
          overflow-hidden
        "
        data-testid="emoji-tabs"
      >
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`
              min-w-0
              text-[12px] sm:text-xs
              font-medium
              px-1 sm:px-1.5
              py-2
              -mb-px
              whitespace-nowrap
              transition-colors
              border-b-2
              focus-visible:outline-none
              focus-visible:text-[#002FA7]
              ${
                tab === t
                  ? 'text-[#002FA7] border-[#002FA7]'
                  : 'text-theme-muted border-transparent hover:text-theme-text'
              }
            `}
            title={t}
            aria-pressed={tab === t}
            data-testid={`emoji-tab-${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div
        className="
          grid grid-cols-6 sm:grid-cols-8
          gap-2 sm:gap-2
          max-h-[min(13rem,32dvh)]
          sm:max-h-[min(18rem,42vh)]
          overflow-y-auto overflow-x-hidden
          pr-0.5
          [-ms-overflow-style:none]
          [scrollbar-width:none]
          [&::-webkit-scrollbar]:hidden
        "
        data-testid="emoji-grid"
      >
        {visibleEmojis.map((e, i) => (
          <button
            key={`${tab}-${i}-${e}`}
            type="button"
            onClick={() => handlePick(e)}
            className="
              flex items-center justify-center
              h-10 w-10
              sm:h-10 sm:w-10
              text-2xl
              rounded-xl
              hover:bg-theme-hover
              active:scale-95
              transition
              focus-visible:outline-none
              focus-visible:ring-2
              focus-visible:ring-[#002FA7]/30
            "
            data-testid={`emoji-${e}`}
          >
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}